/**
 * AddToCalendar — Sprint 84
 *
 * Bouton réutilisable "Ajouter à mon agenda" qui propose Google Calendar,
 * Outlook/Microsoft Web et un téléchargement ICS (Apple/Thunderbird/etc.).
 *
 * Props:
 *   - title:        string (required)
 *   - description:  string (optional) — typically merged with venue / virtual link
 *   - location:     string (optional)
 *   - startDate:    string ISO date or YYYY-MM-DD (required)
 *   - endDate:      string ISO date or YYYY-MM-DD (optional → +1h default for timed, +1d for all-day)
 *   - virtualLink:  string (optional) — appended to description (Zoom/Teams URL)
 *   - virtualPassword: string (optional) — appended to description
 *   - testId:       string (optional) — root data-testid
 */
import { useEffect, useRef, useState } from "react";
import { Calendar, ChevronDown, X } from "lucide-react";

const GOOGLE_LOGO = "https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg";
const OUTLOOK_LOGO = "https://upload.wikimedia.org/wikipedia/commons/d/df/Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg";

/** Convert a YYYY-MM-DD or full ISO datetime to YYYYMMDD[THHmmssZ] (UTC). */
function toCalendarFormat(input, isAllDay = false) {
  if (!input) return "";
  const trimmed = String(input).trim();
  // If it's just YYYY-MM-DD → all-day event
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  if (dateOnly || isAllDay) {
    return trimmed.replace(/-/g, "");
  }
  // Otherwise parse as Date and emit ISO compact UTC.
  const d = new Date(trimmed);
  if (isNaN(d.getTime())) return trimmed.replace(/[-:]/g, "").replace("T", "T").replace(/\.\d+/, "");
  const pad = (n) => String(n).padStart(2, "0");
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

/** Add `hours` to a datetime string, return ISO. */
function addHours(input, hours) {
  if (!input) return "";
  const d = new Date(input);
  if (isNaN(d.getTime())) return input;
  d.setUTCHours(d.getUTCHours() + hours);
  return d.toISOString();
}

/** Add `days` to a YYYY-MM-DD date. */
function addDays(dateStr, days) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

function buildDescription({ description, virtualLink, virtualPassword, location }) {
  const lines = [];
  if (description) lines.push(description);
  if (virtualLink) {
    lines.push("");
    lines.push(`🔗 Lien de connexion : ${virtualLink}`);
    if (virtualPassword) lines.push(`🔐 Code d'accès : ${virtualPassword}`);
  } else if (location) {
    lines.push("");
    lines.push(`📍 Lieu : ${location}`);
  }
  return lines.join("\n");
}

function buildGoogleUrl({ title, description, location, startDate, endDate, isAllDay, virtualLink, virtualPassword }) {
  const start = toCalendarFormat(startDate, isAllDay);
  let end = endDate ? toCalendarFormat(endDate, isAllDay) : "";
  if (!end) {
    end = isAllDay
      ? toCalendarFormat(addDays(String(startDate).slice(0, 10), 1), true)
      : toCalendarFormat(addHours(startDate, 1));
  }
  const url = new URL("https://calendar.google.com/calendar/render");
  url.searchParams.set("action", "TEMPLATE");
  url.searchParams.set("text", title);
  url.searchParams.set("dates", `${start}/${end}`);
  url.searchParams.set("details", buildDescription({ description, virtualLink, virtualPassword, location }));
  if (location) url.searchParams.set("location", location);
  if (virtualLink) url.searchParams.set("location", virtualLink); // overrides physical for virtual
  return url.toString();
}

function buildOutlookUrl({ title, description, location, startDate, endDate, isAllDay, virtualLink, virtualPassword }) {
  // Outlook Web compose URL — accepts ISO datetimes.
  let start = startDate;
  let end = endDate || (isAllDay ? addDays(String(startDate).slice(0, 10), 1) : addHours(startDate, 1));
  if (isAllDay) {
    start = String(start).slice(0, 10);
    end = String(end).slice(0, 10);
  }
  const url = new URL("https://outlook.live.com/calendar/0/deeplink/compose");
  url.searchParams.set("path", "/calendar/action/compose");
  url.searchParams.set("rru", "addevent");
  url.searchParams.set("subject", title);
  url.searchParams.set("startdt", start);
  url.searchParams.set("enddt", end);
  url.searchParams.set("body", buildDescription({ description, virtualLink, virtualPassword, location }));
  if (location || virtualLink) url.searchParams.set("location", virtualLink || location || "");
  if (isAllDay) url.searchParams.set("allday", "true");
  return url.toString();
}

function buildIcs({ title, description, location, startDate, endDate, isAllDay, virtualLink, virtualPassword }) {
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@lawinetech.com`;
  const dtStart = toCalendarFormat(startDate, isAllDay);
  let dtEnd = endDate ? toCalendarFormat(endDate, isAllDay) : "";
  if (!dtEnd) {
    dtEnd = isAllDay
      ? toCalendarFormat(addDays(String(startDate).slice(0, 10), 1), true)
      : toCalendarFormat(addHours(startDate, 1));
  }
  const desc = buildDescription({ description, virtualLink, virtualPassword, location }).replace(/\n/g, "\\n");
  const loc = (virtualLink || location || "").replace(/\n/g, "\\n");
  const dateMarker = isAllDay ? ";VALUE=DATE" : "";
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//La WineTech//FR",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${toCalendarFormat(new Date().toISOString())}`,
    `DTSTART${dateMarker}:${dtStart}`,
    `DTEND${dateMarker}:${dtEnd}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${desc}`,
    `LOCATION:${loc}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

function downloadIcs(filename, content) {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export default function AddToCalendar(props) {
  const { title, testId = "add-to-calendar" } = props || {};
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!title || !props.startDate) return null;

  // Detect all-day from inputs (YYYY-MM-DD format)
  const isAllDay = /^\d{4}-\d{2}-\d{2}$/.test(String(props.startDate).trim());
  const enrichedProps = { ...props, isAllDay };

  const googleUrl = buildGoogleUrl(enrichedProps);
  const outlookUrl = buildOutlookUrl(enrichedProps);

  const handleIcs = () => {
    const ics = buildIcs(enrichedProps);
    const safeName = String(title).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "event";
    downloadIcs(`${safeName}.ics`, ics);
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={ref} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="inline-flex items-center gap-1.5 rounded-full border-2 border-wine-200 bg-white px-4 py-2 text-sm font-bold text-wine-700 transition hover:border-wine-400 hover:bg-wine-50"
        data-testid={`${testId}-trigger`}
      >
        <Calendar className="h-4 w-4" /> Ajouter à mon agenda <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl"
          data-testid={`${testId}-menu`}
        >
          <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-2.5">
            <span className="text-xs font-bold uppercase tracking-wider text-neutral-500">Choisir un service</span>
            <button onClick={() => setOpen(false)} className="grid h-6 w-6 place-items-center rounded-full text-neutral-400 hover:bg-neutral-100" aria-label="Fermer"><X className="h-3.5 w-3.5" /></button>
          </div>
          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50"
            data-testid={`${testId}-google`}
          >
            <img src={GOOGLE_LOGO} alt="Google Calendar" className="h-5 w-5 flex-shrink-0" />
            <div className="flex-1">
              <div>Google Calendar</div>
              <div className="text-[10px] font-normal text-neutral-500">Pré-remplit l&apos;événement</div>
            </div>
          </a>
          <a
            href={outlookUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50"
            data-testid={`${testId}-outlook`}
          >
            <img src={OUTLOOK_LOGO} alt="Outlook" className="h-5 w-5 flex-shrink-0" />
            <div className="flex-1">
              <div>Outlook / Microsoft</div>
              <div className="text-[10px] font-normal text-neutral-500">Calendrier Microsoft 365</div>
            </div>
          </a>
          <button
            onClick={handleIcs}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-neutral-800 hover:bg-neutral-50"
            data-testid={`${testId}-ics`}
          >
            <Calendar className="h-5 w-5 flex-shrink-0 text-neutral-500" />
            <div className="flex-1">
              <div>Apple Calendar / autre (.ics)</div>
              <div className="text-[10px] font-normal text-neutral-500">Fichier universel téléchargeable</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
